import React from "react";
import classnames from "classnames";
import ReactSwitch from "react-switch";
import { toast } from "react-toastify";
import { CheckboxIcon, EyeIcon, TrashIcon } from "@aureskonnect/react-ui";
import { useSnapshot } from "valtio";
import { useTranslation } from "react-i18next";
import { mutate } from "swr";
import { Tooltip } from "@mui/material";
import { uuid } from "uuidv4";

import store, {
    setGlobalDataIsUpdated,
    setIsDataUpdated,
    setIsHistoryShopsModalOpened,
    setSelectedPaymentMethods,
    setSelectedHistoryPaymentMethods,
    setIsLoading,
    setKey,
} from "../store";
import { store as dataStore } from "@components/VerticalLayout/store";

import TimePastSvgIcon from "@components/Common/SvgIcons/TimePastSvgIcon";
import ErrorToast from "@components/Common/ErrorTost";
import {
    setActivationDeactivationMessageModalOpened,
    setMessage,
    setYesFunction,
} from "@components/Common/ConfirmationActivationDeactivationMessageModal/store";
import { generalConfigStore, setItemsDisable } from "@store";

type CustomIconsType = {
    selectedRowData: any;
    isArchivedPaymentMethodsClicked: boolean;
    isArchivedValidationModalOpened: boolean;
    setIsArchivedValidationModalOpened: Function;
    setSelectedRows: Function;
};

export function CustomIcons({
    selectedRowData,
    isArchivedPaymentMethodsClicked,
    isArchivedValidationModalOpened,
    setIsArchivedValidationModalOpened,
    setSelectedRows,
}: CustomIconsType) {
    const { t } = useTranslation();
    const { franchiseID, userID, operatorID } = useSnapshot(dataStore);
    const {
        isHistoryShopsModalOpened,
        paymentMethodsData,
        modify,
        isLoading,
    } = useSnapshot(store);
    const { itemsDisable } = useSnapshot(generalConfigStore);
    const [choiceState, setPaymentMethodState] = React.useState<boolean>(
        selectedRowData.selectedRow.isActive
    );

    const i18nextLng = localStorage.getItem("i18nextLng") || "fr";
    const uuidUser = localStorage.getItem("uuidUser");

    async function handleStateChange(data: boolean) {
        setIsLoading(true);
        let dataConfig = {
            userId: userID,
            shopId: [selectedRowData.selectedRow.idShop],
            franchiseId: franchiseID,
            operatorId: operatorID,
            uuidUser: uuidUser,
            data: {
                [selectedRowData.selectedRow.id]: {
                    designation:
                        selectedRowData.selectedRow[t("Payment method")],
                    currency: selectedRowData.selectedRow.Devise,
                    display: selectedRowData.selectedRow[t("Display name")],
                    shopId: selectedRowData.selectedRow.idShop,
                    saleSupport:
                        selectedRowData.selectedRow[t("Support de vente")],
                    country: selectedRowData.selectedRow.Pays,
                    tag: selectedRowData.selectedRow.Tag,
                    isArchived: selectedRowData.selectedRow.isArchived,
                    isActive: data,
                    languages: selectedRowData.selectedRow.languages,
                    advancedDisplayName:
                        selectedRowData.selectedRow.advancedDisplayName,
                },
            },
        };
        const isStateChange = true;
        const apiUrl = `${process.env.REACT_APP_API_V2_URL}/settings/general/paymentMethod`;

        const requestOptions = {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ dataConfig, isStateChange }),
        };
        mutate(
            apiUrl,
            await fetch(apiUrl, requestOptions)
                .then((response) => response.json())
                .then((result) => {
                    if (result.error) {
                        throw Error(result.message);
                    }
                    if (
                        data === false &&
                        itemsDisable.find(
                            (item: any) =>
                                item.id === selectedRowData.selectedRow.id &&
                                item.shopId ===
                                    selectedRowData.selectedRow.idShop
                        ) === undefined
                    ) {
                        const item = [
                            ...JSON.parse(JSON.stringify(itemsDisable)),
                            {
                                id: selectedRowData.selectedRow.id,
                                state: !choiceState,
                                shopId: selectedRowData.selectedRow.idShop,
                            },
                        ];
                        setItemsDisable(item);
                    } else if (data === true) {
                        const item = itemsDisable.filter(
                            (item: any) =>
                                !(
                                    item.id ===
                                        selectedRowData.selectedRow.id &&
                                    item.shopId ===
                                        selectedRowData.selectedRow.idShop
                                )
                        );
                        setItemsDisable(item);
                    }
                    toast.success(
                        data === true
                            ? `${t(
                                  "Payment method has been successfully activated"
                              )}`
                            : `${t(
                                  "Payment method has been successfully deactivated"
                              )}`,
                        {
                            position: toast.POSITION.TOP_CENTER,
                            autoClose: 2000,
                            theme: "colored",
                            closeOnClick: true,
                            pauseOnHover: true,
                            draggable: true,
                            icon: (
                                <CheckboxIcon
                                    height={25}
                                    width={25}
                                    fill="white"
                                />
                            ),
                        }
                    );
                    setGlobalDataIsUpdated!(true);
                    setIsDataUpdated(true);
                    setIsLoading(false);
                })
                .catch((error) => {
                    setIsLoading(false);
                    toast.error(`${t("There's an error")!}`, {
                        position: toast.POSITION.TOP_CENTER,
                        autoClose: 2000,
                        theme: "colored",
                        closeOnClick: true,
                        pauseOnHover: true,
                        draggable: true,
                    });
                })
        );
    }

    async function handleActivePaymentMethod(selectedRowData: any) {
        let dataConfig = {
            userId: userID,
            shopId: [selectedRowData.selectedRow.idShop],
            franchiseId: franchiseID,
            operatorId: operatorID,
            uuidUser: uuidUser,
            data: {
                [selectedRowData.selectedRow.id]: {
                    designation:
                        selectedRowData.selectedRow[t("Payment method")],
                    currency: selectedRowData.selectedRow.Devise,
                    display: selectedRowData.selectedRow[t("Display name")],
                    shopId: selectedRowData.selectedRow.idShop,
                    saleSupport:
                        selectedRowData.selectedRow[t("Support de vente")],
                    country: selectedRowData.selectedRow.Pays,
                    tag: selectedRowData.selectedRow.tag,
                    isActive: selectedRowData.selectedRow.isActive,
                    isArchived: false,
                    languages: selectedRowData.selectedRow.languages,
                    advancedDisplayName:
                        selectedRowData.selectedRow.advancedDisplayName,
                },
            },
        };
        const apiUrl = `${process.env.REACT_APP_API_V2_URL}/settings/general/paymentMethod`;

        const requestOptions = {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(dataConfig),
        };
        mutate(
            apiUrl,
            await fetch(apiUrl, requestOptions)
                .then((response) => response.json())
                .then((result) => {
                    if (result.error) {
                        throw Error(result.message);
                    }

                    if (
                        selectedRowData.selectedRow.isActive === false &&
                        itemsDisable.find(
                            (item: any) =>
                                item.id === selectedRowData.selectedRow.id &&
                                item.shopId ===
                                    selectedRowData.selectedRow.idShop
                        ) === undefined
                    ) {
                        const item = [
                            ...JSON.parse(JSON.stringify(itemsDisable)),
                            {
                                id: selectedRowData.selectedRow.id,
                                state: false,
                                shopId: selectedRowData.selectedRow.idShop,
                            },
                        ];
                        setItemsDisable(item);
                    }

                    toast.success(
                        `${t("The Payment method is successfully activated")}`,
                        {
                            position: toast.POSITION.TOP_CENTER,
                            autoClose: 2000,
                            theme: "colored",
                            closeOnClick: true,
                            pauseOnHover: true,
                            draggable: true,
                            icon: (
                                <CheckboxIcon
                                    height={25}
                                    width={25}
                                    fill="white"
                                />
                            ),
                        }
                    );
                    setGlobalDataIsUpdated!(true);
                    setIsDataUpdated(true);
                    setKey(uuid());
                    setIsLoading(false);
                })
                .catch((error) => {
                    toast.error(`${t("There's an error")!}`, {
                        position: toast.POSITION.TOP_CENTER,
                        autoClose: 2000,
                        theme: "colored",
                        closeOnClick: true,
                        pauseOnHover: true,
                        draggable: true,
                    });
                    setIsLoading(false);
                })
        );
    }
    async function isSaleSupportArchived() {
        toast.dismiss();
        let isSaleSupportArchived = false;
        const url = `${process.env.REACT_APP_API_V2_URL}/settings/general/saleSupport?userId=${userID}&franchiseId=${franchiseID}&type=archived&uuidUser=${uuidUser}`;
        mutate(
            url,
            await fetch(url, {
                method: "GET",
                headers: { "Accept-Language": i18nextLng },
            })
                .then((response) => response.json())
                .then((data) => {
                    data.data.forEach((elt: any) => {
                        selectedRowData.selectedRow[t("Sales support")]
                            .split(",")
                            .forEach((support: any) => {
                                if (
                                    elt.subRows.some(
                                        (sale: any) =>
                                            `${sale[t("Sale support")]} ${
                                                sale.shopId
                                            }` ===
                                            `${support} ${selectedRowData.selectedRow.idShop}`
                                    )
                                ) {
                                    isSaleSupportArchived = true;
                                }
                            });
                    });
                })
                .catch((error) => {
                    toast.error(`${t("There's an error")!}`, {
                        position: toast.POSITION.TOP_CENTER,
                        autoClose: 2000,
                        theme: "colored",
                        closeOnClick: true,
                        pauseOnHover: true,
                        draggable: true,
                    });
                })
        );

        return isSaleSupportArchived;
    }

    async function isInformationModeConsumed() {
        toast.dismiss();
        let isInformationModeUsed = false;
        const url = `${process.env.REACT_APP_API_V2_URL}/settings/consumedSettingGeneral?shopId=${selectedRowData.selectedRow.idShop}&franchiseId=${franchiseID}&type=meansOfPayment`;
        mutate(
            url,
            await fetch(url, {
                method: "GET",
                headers: { "Content-Type": "application/json" },
            })
                .then((response) => response.json())
                .then((result) => {
                    if (
                        result.data.some(
                            (el: any) =>
                                el.id === selectedRowData.selectedRow.id
                        )
                    ) {
                        isInformationModeUsed = true;
                    }
                })
                .catch((error) => {
                    toast.error(`${t("There's an error")!}`, {
                        position: toast.POSITION.TOP_CENTER,
                        autoClose: 2000,
                        theme: "colored",
                        closeOnClick: true,
                        pauseOnHover: true,
                        draggable: true,
                    });
                })
        );
        return isInformationModeUsed;
    }
    return (
        <React.Fragment>
            {selectedRowData.selectedRow?.subRows === undefined ? (
                <div className="d-flex justify-content-around">
                    {modify === true ? (
                        <>
                            <Tooltip
                                title={
                                    isArchivedPaymentMethodsClicked
                                        ? ""
                                        : choiceState === false
                                        ? t("Activate")
                                        : t("Unactivate")
                                }
                            >
                                <div>
                                    <ReactSwitch
                                        uncheckedIcon={false}
                                        checkedIcon={false}
                                        handleDiameter={26}
                                        offColor="#f7b4b8"
                                        offHandleColor="#E30613"
                                        checked={choiceState}
                                        onChange={async (e: boolean) => {
                                            setMessage(
                                                choiceState
                                                    ? t(
                                                          "Caution, the Payment method may be used by other modules.\n Would you like to confirm the deactivation of the selected Payment method?"
                                                      )
                                                    : t(
                                                          "Would you like to confirm the activation of the selected Payment method?"
                                                      )
                                            );
                                            setYesFunction(() => {
                                                setPaymentMethodState(
                                                    !choiceState
                                                );
                                                handleStateChange(e);
                                            });
                                            setActivationDeactivationMessageModalOpened(
                                                true
                                            );
                                        }}
                                        onColor="#c2eddd"
                                        onHandleColor="#34C38F"
                                        width={50}
                                        height={20}
                                        className={`${classnames(
                                            "cmn_icn_BTenableDisable",
                                            {
                                                "not-allowed-icon__clz": isArchivedPaymentMethodsClicked,
                                            }
                                        )}`}
                                    />
                                </div>
                            </Tooltip>
                            <div>
                                {!isArchivedPaymentMethodsClicked ? (
                                    <Tooltip title={t("Archive")}>
                                        <div>
                                            <TrashIcon
                                                onClick={async () => {
                                                    let isConsumed = await isInformationModeConsumed();
                                                    if (isConsumed === true) {
                                                        ErrorToast(
                                                            t(
                                                                "Please note, you cannot archive an element used in a template"
                                                            )
                                                        );
                                                        setIsLoading(false);
                                                    } else {
                                                        setSelectedRows([
                                                            selectedRowData.selectedRow,
                                                        ]);
                                                        setIsArchivedValidationModalOpened(
                                                            !isArchivedValidationModalOpened
                                                        );
                                                    }
                                                }}
                                                height={25}
                                                width={25}
                                                fill="red"
                                                style={{ cursor: "pointer" }}
                                                className={`${classnames(
                                                    "ml-2 cmn_icn_BTarchived",
                                                    {
                                                        "not-allowed-icon__clz": isLoading,
                                                    }
                                                )}`}
                                            />
                                        </div>
                                    </Tooltip>
                                ) : (
                                    <Tooltip
                                        title={
                                            paymentMethodsData.find(
                                                (el: any) =>
                                                    el.id ===
                                                    selectedRowData.selectedRow
                                                        .id
                                            ) !== undefined
                                                ? ""
                                                : t("Unarchive")
                                        }
                                    >
                                        <div>
                                            <EyeIcon
                                                height={25}
                                                width={25}
                                                onClick={async (e: any) => {
                                                    setIsLoading(true);
                                                    let isArchived = await isSaleSupportArchived();
                                                    if (isArchived === true) {
                                                        ErrorToast(
                                                            `${t(
                                                                "Please note that  the unarchiving process could not be successfully completed. Please check the settings of the store(s) data."
                                                            )!}`
                                                        );
                                                        setIsLoading(false);
                                                    } else {
                                                        handleActivePaymentMethod(
                                                            selectedRowData
                                                        );
                                                    }
                                                }}
                                                className={`${classnames(
                                                    "pointer__clz ml-2 cmn_icn_BTunarchive",
                                                    {
                                                        "not-allowed-icon__clz":
                                                            paymentMethodsData.find(
                                                                (el: any) =>
                                                                    el.id ===
                                                                    selectedRowData
                                                                        .selectedRow
                                                                        .id
                                                            ) !== undefined ||
                                                            isLoading,
                                                    }
                                                )}`}
                                            />
                                        </div>
                                    </Tooltip>
                                )}
                            </div>
                        </>
                    ) : null}
                    <Tooltip title={t("Consult history")}>
                        <div>
                            <TimePastSvgIcon
                                onClick={() => {
                                    setIsHistoryShopsModalOpened(
                                        !isHistoryShopsModalOpened
                                    );
                                    setSelectedHistoryPaymentMethods(
                                        selectedRowData.selectedRow
                                    );
                                    setSelectedPaymentMethods([
                                        {
                                            [selectedRowData.selectedRow
                                                .idShop]:
                                                selectedRowData.selectedRow.id,
                                        },
                                    ]);
                                }}
                                className="pointer__clz ml-2 cmn_icn_BThistory"
                            />
                        </div>
                    </Tooltip>
                </div>
            ) : null}
        </React.Fragment>
    );
}
